import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { crmApi, errMsg } from '@crm/api';

/**
 * Слайс календаря. Для упрощения храним:
 *   byBranch[branchId] = {
 *     range: { from, to },              // текущий загруженный диапазон
 *     events: CalendarEvent[],          // events в range
 *     recordsByDay: { [YYYY-MM-DD]: Record[] },
 *     status, error
 *   }
 */

const emptyBranch = () => ({
  range: null,
  events: [],
  recordsByDay: {},
  status: 'idle',
  error: null,
  saveStatus: 'idle',
  saveError: null,
});

const getBranch = (state, branchId) => {
  if (!state.byBranch[branchId]) state.byBranch[branchId] = emptyBranch();
  return state.byBranch[branchId];
};

export const fetchEvents = createAsyncThunk(
  'crmCalendar/fetchEvents',
  async ({ branchId, from, to }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).get('/calendar', { params: { from, to } });
      return { branchId, from, to, events: data.events ?? [] };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось загрузить календарь'));
    }
  },
);

export const createEvent = createAsyncThunk(
  'crmCalendar/createEvent',
  async ({ branchId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).post('/calendar', payload);
      return { branchId, event: data.event };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось создать событие'));
    }
  },
);

export const updateEvent = createAsyncThunk(
  'crmCalendar/updateEvent',
  async ({ branchId, id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).patch(`/calendar/${id}`, payload);
      return { branchId, event: data.event };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось обновить событие'));
    }
  },
);

export const transferEvent = createAsyncThunk(
  'crmCalendar/transferEvent',
  async ({ branchId, id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).post(`/calendar/${id}/transfer`, payload);
      return { branchId, sourceEvent: data.sourceEvent, event: data.event };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось перенести событие'));
    }
  },
);

export const deleteEvent = createAsyncThunk(
  'crmCalendar/deleteEvent',
  async ({ branchId, id }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).delete(`/calendar/${id}`);
      return { branchId, id, event: data.event ?? null };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось отменить событие'));
    }
  },
);

export const restoreEvent = createAsyncThunk(
  'crmCalendar/restoreEvent',
  async ({ branchId, id }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).post(`/calendar/${id}/restore`);
      return { branchId, event: data.event };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось вернуть событие'));
    }
  },
);

export const fetchRecords = createAsyncThunk(
  'crmCalendar/fetchRecords',
  async ({ branchId, day }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).get(`/calendar/records/${day}`);
      return { branchId, day, records: data.records ?? [] };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось загрузить записи'));
    }
  },
);

export const createRecord = createAsyncThunk(
  'crmCalendar/createRecord',
  async ({ branchId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).post('/calendar/records', payload);
      return { branchId, day: payload.day, record: data.record };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось создать запись'));
    }
  },
);

export const deleteRecord = createAsyncThunk(
  'crmCalendar/deleteRecord',
  async ({ branchId, day, id }, { rejectWithValue }) => {
    try {
      await crmApi(branchId).delete(`/calendar/records/${id}`);
      return { branchId, day, id };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось удалить запись'));
    }
  },
);

export const markVisit = createAsyncThunk(
  'crmCalendar/markVisit',
  async ({ branchId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).post('/calendar/visitings', payload);
      return { branchId, visit: data.visit };
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось отметить посещение'));
    }
  },
);

const slice = createSlice({
  name: 'crmCalendar',
  initialState: { byBranch: {} },
  reducers: {
    clear: (state, action) => {
      const branchId = action.payload;
      if (branchId) delete state.byBranch[branchId];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state, action) => {
        const s = getBranch(state, action.meta.arg.branchId);
        s.status = 'loading';
        s.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        const { branchId, from, to, events } = action.payload;
        const s = getBranch(state, branchId);
        s.status = 'idle';
        s.range = { from, to };
        s.events = events;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        const s = getBranch(state, action.meta.arg.branchId);
        s.status = 'error';
        s.error = action.payload;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        const { branchId, event } = action.payload;
        const s = getBranch(state, branchId);
        s.events = [...s.events, event];
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        const { branchId, event } = action.payload;
        const s = getBranch(state, branchId);
        s.events = s.events.map((e) => (e.id === event.id ? event : e));
      })
      .addCase(transferEvent.fulfilled, (state, action) => {
        const { branchId, sourceEvent, event } = action.payload;
        const s = getBranch(state, branchId);
        const inRange = (item) => !s.range || (item.day >= s.range.from && item.day <= s.range.to);
        s.events = s.events.map((e) => (e.id === sourceEvent.id ? sourceEvent : e));
        if (event && inRange(event)) {
          s.events = s.events.some((e) => e.id === event.id)
            ? s.events.map((e) => (e.id === event.id ? event : e))
            : [...s.events, event];
        }
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        const { branchId, id, event } = action.payload;
        const s = getBranch(state, branchId);
        s.events = event
          ? s.events.map((e) => (e.id === event.id ? event : e))
          : s.events.filter((e) => e.id !== id);
      })
      .addCase(restoreEvent.fulfilled, (state, action) => {
        const { branchId, event } = action.payload;
        const s = getBranch(state, branchId);
        s.events = s.events.map((e) => (e.id === event.id ? event : e));
      })
      .addCase(fetchRecords.fulfilled, (state, action) => {
        const { branchId, day, records } = action.payload;
        getBranch(state, branchId).recordsByDay[day] = records;
      })
      .addCase(createRecord.fulfilled, (state, action) => {
        const { branchId, day, record } = action.payload;
        const s = getBranch(state, branchId);
        s.recordsByDay[day] = [...(s.recordsByDay[day] ?? []), record];
      })
      .addCase(deleteRecord.fulfilled, (state, action) => {
        const { branchId, day, id } = action.payload;
        const s = getBranch(state, branchId);
        s.recordsByDay[day] = (s.recordsByDay[day] ?? []).filter((r) => r.id !== id);
      });
  },
});

export const calendarActions = slice.actions;

export const calendarSelectors = {
  selectEvents: (branchId) => (state) => state.crmCalendar.byBranch[branchId]?.events ?? [],
  selectStatus: (branchId) => (state) => state.crmCalendar.byBranch[branchId]?.status ?? 'idle',
  selectError: (branchId) => (state) => state.crmCalendar.byBranch[branchId]?.error ?? null,
  selectRange: (branchId) => (state) => state.crmCalendar.byBranch[branchId]?.range ?? null,
  selectRecords: (branchId, day) => (state) =>
    state.crmCalendar.byBranch[branchId]?.recordsByDay[day] ?? [],
};

export default slice.reducer;
